function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = []
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          "form",
          `${element.form.getAttribute("id")}-${uniqueKey}`
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open")
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    );
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*"
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === "minus"
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle(
        "disabled",
        parseInt(value) <= parseInt(this.input.min)
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (let i = 0, count = selector.options.length; i < count; i++) {
    const option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button):not(.button-show-more)"
    ).forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector("summary")
        )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`
        );
      }
    } else {
      // setTimeout(() => {
      //   detailsElement.classList.add("menu-opening");
      //   summaryElement.setAttribute("aria-expanded", true);
      //   parentMenuElement && parentMenuElement.classList.add("submenu-open");
      //   !reducedMotion || reducedMotion.matches
      //     ? addTrapFocus()
      //     : summaryElement.nextElementSibling.addEventListener(
      //         "transitionend",
      //         addTrapFocus
      //       );
      // }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details:not(.mobile-facets__disclosure):not(.mobile-facets__details)").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`
    );
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset ||
      this.closest(".header-wrapper").classList.contains(
        "header-wrapper--border-bottom"
      )
        ? 1
        : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(
        this.header.getBoundingClientRect().bottom - this.borderOffset
      )}px`
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(
          this.header.getBoundingClientRect().bottom - this.borderOffset
        )}px`
      );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`
    );
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false)
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            const sourceQty = html.querySelector(
              ".quick-order-list-container"
            ).parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(
        `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`
      )
    );
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(
        this.querySelector("template").content.firstElementChild.cloneNode(true)
      );

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(
        content.querySelector("video, model-viewer, iframe")
      );
      if (focus) deferredElement.focus();
      if (
        deferredElement.nodeName == "VIDEO" &&
        deferredElement.getAttribute("autoplay")
      ) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft -
      this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
        this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1]
      )
    ) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link")
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this))
    );
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      );
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true")
          this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true")
      this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener(
        "click",
        this.autoPlayToggle.bind(this)
      );
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add(
      "slider-counter__link--active"
    );
    this.sliderControlButtons[this.currentPage - 1].setAttribute(
      "aria-current",
      true
    );
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (
      !this.reducedMotion.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed
    );
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow
      );
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext =
      (button === "next" && !isLastSlide) ||
      (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    currentSlide?.classList.add(`${animationClassOut}-${direction}`);
    nextSlide?.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide?.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide?.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) +
          1 -
          this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.soldOutOptions = this.dataset.soldoutOptions && this.dataset.soldoutOptions !== '' ? JSON.parse(this.dataset.soldoutOptions) : null;
  }

  getProductInfoContainer() {
    // Find the closest product-info element to properly scope queries
    return this.closest('product-info');
  }

  getSubmitButton() {
    // Find submit button scoped to this product instance
    const productInfo = this.getProductInfoContainer();
    const context = productInfo || this;
    return context.querySelector('.product-form__submit') || this.parentNode?.querySelector('.product-form__submit');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('.json_variants[type="application/json"]').textContent);
    return this.variantData;
  }
  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => {

        return this.options[index] === option;
      }).includes(false);
    });
  }

  updateOptions() {

    this.options = Array.from(this.querySelectorAll('select, fieldset'), (element) => {
      if (element.tagName === 'SELECT') {
        return element.value;
      }
      if (element.tagName === 'FIELDSET') {
        return Array.from(element.querySelectorAll('input')).find((radio) => radio.checked)?.value;
      }
    });

  }
  //update soldout option class
  // updateSoldOutOptionClass(target){
  //   console.log("here change listener", target)
  //   this?.querySelectorAll('input[data-option-index]')?.length && [...this.querySelectorAll('input[data-option-index]')].forEach(el => el.classList.remove('soldout-option'));
  //   const optionIndex = target.dataset.optionIndex;
  //   let currentSoldOutOptions = this.soldOutOptions.filter(option => option[optionIndex] === target.value);

  //   console.log("currentSoldOutOptions", currentSoldOutOptions ,  this.currentVariant)

  //   if(currentSoldOutOptions.length && currentSoldOutOptions[0].length === 3){
  //     [...this.querySelectorAll('input[data-option-index]')].forEach(elOther => {
  //       if(elOther.checked && elOther.dataset.index !== optionIndex){
  //         currentSoldOutOptions = currentSoldOutOptions.filter(option => option[elOther.dataset.optionIndex] === elOther.value);
  //         return false;
  //       }
  //     });
  //   }

  //   currentSoldOutOptions.forEach((soldOutOption) => {
  //     const isMatch = this.currentVariant.options.every((option, index) => {
  //         return soldOutOption.includes(option);
  //     });

  //     if (isMatch) {
  //         soldOutOption.forEach((item, index) => {
  //             console.log("Matching soldout option:", item, this.currentVariant);
  //             this.querySelector(`input[data-option-index="${index}"][value="${item}"]`)?.classList.add('soldout-option');
  //         });
  //     }
  // });
  updateSoldOutOptionClass() {
    const selectedOptions = {};
  
    // Gather currently selected options
    this.querySelectorAll('input[data-option-index]:checked').forEach(el => {
      selectedOptions[el.dataset.optionIndex] = el.value;
    });
  
    // Get how many total option sets there are
    const totalOptionCount = this.querySelectorAll('input[data-option-index]').length / 
                             this.querySelectorAll('[data-option-index="0"]').length;
  
    // Clear previous states
    this.querySelectorAll('input[data-option-index]').forEach(el => {
      el.classList.remove('soldout-option', 'disabled');
    });
  
    // Evaluate each input option
    this.querySelectorAll('input[data-option-index]').forEach(input => {
      const optionIndex = input.dataset.optionIndex;
      const optionValue = input.value;
  
      const simulatedSelection = {
        ...selectedOptions,
        [optionIndex]: optionValue
      };
  
      // Only run logic if all options are selected after simulating
      if (Object.keys(selectedOptions).length < 1) return;
  
      const isSoldOut = this.soldOutOptions.some(soldOutOption => {
        return Object.entries(simulatedSelection).every(([i, val]) => soldOutOption[i] === val);
      });
  
      if (isSoldOut) {
        input.classList.add('soldout-option', 'disabled');
      }
    });
  }
  
  



  connectedCallback() {
    //init variant pills update
    this.updateOptions();
    this.updateMasterId();
    this.checkIfAlloptionsChecked('remove');

    // bottom code generate problem with soldout options on first load !

    // this.querySelectorAll('input[data-option-index]').forEach((input) => {
    //   this.updateSoldOutOptionClass(input);
    // });

    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateOptions();
      this.updateMasterId();
      this.updateSelectionMetadata(event);
      this.updateSoldOutOptionClass();
      this.checkIfAlloptionsChecked('remove');

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.completeOptionValuesSet,
        },
      });
    });
  }
  // check if all inputs checked
  checkIfAlloptionsChecked(action = null){
    const notCheckedInputs = [];
    const submitButton = this.getSubmitButton();
    this?.querySelectorAll('.product-form__input[data-option-name]')?.length && [...this.querySelectorAll('.product-form__input[data-option-name]')].forEach(el => {
      if(!el.querySelector('input[data-option-index]:checked')){
        notCheckedInputs.push(el.dataset.optionName);
      }
    });

    // Only add variant:changed listener once per instance
    if (!this.variantChangeListenerAdded) {
      this.variantChangeListenerAdded = true;
      window.addEventListener('variant:changed', (event) => {
        // Only process if this event is for this product instance
        const productInfo = this.getProductInfoContainer();
        if (!productInfo || !event.target || !(event.target instanceof Node) || !productInfo.contains(event.target)) {
          return;
        }

        let variantsData = this.getVariantData();
        let currentVariant = event.detail.variantId;
        let submitButton = this.getSubmitButton();
        let submitButtonText = submitButton?.querySelector("span");

        if(!submitButtonText) return;

        let foundVariant = variantsData.find(variant => variant.id === currentVariant);

        if (foundVariant) {
          if (foundVariant.available) {
            submitButtonText.textContent = window.variantStrings.addToCart
          } else {
            submitButtonText.textContent = window.variantStrings.soldOut
            submitButton?.setAttribute('disabled', 'disabled');
          }
        }
      });
    }

    if(action === 'remove'){
      if(notCheckedInputs?.length){
        // submitButton.classList.add("atc-disable");
      } else {
        submitButton?.classList.remove("atc-disable");
        submitButton?.removeAttribute('disabled');
      }
      submitButton?.parentNode?.querySelector('.selection-warning')?.remove();
    } else {
      if(notCheckedInputs?.length){
        const warningMessage = document.createElement("span");
        warningMessage.className = "selection-warning";
        warningMessage.textContent = `Please select ${notCheckedInputs.join(', ')} ${notCheckedInputs.length > 1 ? 'options' : 'option'}`;
        submitButton?.parentNode.insertBefore(warningMessage, submitButton);
        submitButton?.classList.add("atc-disable");
        submitButton?.setAttribute('disabled', 'disabled');
      } else {
        submitButton?.classList.remove("atc-disable");
        submitButton?.removeAttribute('disabled');
        submitButton?.parentNode?.querySelector('.selection-warning')?.remove();
      }
    }
  }

  get completeOptionValuesSet() {
    const selectionOptionValues = this.selectedOptionValues;
    const optionGroups = Array.from(this.querySelectorAll("fieldset, select"));
    if (selectionOptionValues.length === optionGroups.length)
      return selectionOptionValues;

    let completeOptionValuesSet = [];
    optionGroups.forEach((group) => {
      const selectedOption = group.querySelector(
        "input:checked",
        "select option[selected]"
      );
      if (selectedOption) {
        completeOptionValuesSet.push(selectedOption.dataset.optionValueId);
      } else {
        const firstAvailableOption = group.querySelector(
          "input:not(:disabled), option:not(:disabled)"
        );
        firstAvailableOption &&
        completeOptionValuesSet.push(
          firstAvailableOption.dataset.optionValueId
        );
      }
    });
    return completeOptionValuesSet;
  }

  // custom code end

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        .querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          swatchValue
        );
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          "unset"
        );
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset"
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector("[data-selected-value]");
      if (selectedSwatchValue) {
        selectedSwatchValue.innerHTML = value;
        selectedSwatchValue.classList.remove("hide");
      }
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked")
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define("variant-selects", VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;
  swiperInstance = null;

  constructor() {
    super();
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    // Show skeleton placeholder immediately
    this.showSkeletonPlaceholder();

    // Start loading in background during browser idle time
    // Note: Prefetch hint is added in related-products.liquid section file
    // This ensures browser pre-fetches recommendations during page load
    // requestIdleCallback then uses the cached response for instant loading
    this.initializeRecommendations(this.dataset.productId);
    window.addEventListener("resize", this.onResize);
  }

  disconnectedCallback() {
    this.observer?.unobserve(this);
    window.removeEventListener("resize", this.onResize);
    this.swiperInstance?.destroy(true, true);
  }

  showSkeletonPlaceholder() {
    // Only show placeholder if section is empty (not already loaded)
    if (this.innerHTML.trim().length > 0) return;

    const heading = this.dataset.heading || 'You may also like';
    const headingSize = this.dataset.headingSize || 'h1';
    const productsCount = parseInt(this.dataset.productsCount) || 4;

    // Create realistic product card skeletons
    const skeletonCards = Array(productsCount).fill('').map(() => `
      <div class="skeleton-card">
        <div class="skeleton-image"></div>
        <div class="skeleton-content">
          <div class="skeleton-title-line"></div>
          <div class="skeleton-title-line short"></div>
          <div class="skeleton-price"></div>
        </div>
      </div>
    `).join('');

    this.innerHTML = `
      <div class="product-recommendations-skeleton">
        <h2 class="related-products__heading inline-richtext heading ${headingSize}">${heading}</h2>
        <div class="skeleton-grid">
          ${skeletonCards}
        </div>
        <style>
          .product-recommendations-skeleton {
            width: 100%;
          }

          .skeleton-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
            gap: 1.5rem;
            padding: 2rem 0;
          }

          @media (min-width: 750px) {
            .skeleton-grid {
              grid-template-columns: repeat(${productsCount}, 1fr);
              gap: 2rem;
            }
          }

          .skeleton-card {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }

          .skeleton-image {
            width: 100%;
            aspect-ratio: 3/4;
            background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
            background-size: 200% 100%;
            animation: shimmer 1.5s ease-in-out infinite;
            border-radius: 4px;
          }

          .skeleton-content {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            padding: 0 0.5rem;
          }

          .skeleton-title-line {
            height: 14px;
            background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
            background-size: 200% 100%;
            animation: shimmer 1.5s ease-in-out infinite;
            border-radius: 3px;
            width: 100%;
          }

          .skeleton-title-line.short {
            width: 70%;
          }

          .skeleton-price {
            height: 18px;
            width: 60px;
            background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
            background-size: 200% 100%;
            animation: shimmer 1.5s ease-in-out infinite;
            border-radius: 3px;
            margin-top: 0.25rem;
          }

          @keyframes shimmer {
            0% {
              background-position: 200% 0;
            }
            100% {
              background-position: -200% 0;
            }
          }
        </style>
      </div>
    `;
  }

  initializeRecommendations(productId) {
    // Strategy: Load in background ASAP so it's ready when user scrolls
    // Use requestIdleCallback to load during browser idle time (after critical content)
    const loadInBackground = () => {
      if (!this.classList.contains('product-recommendations--loaded')) {
        this.loadRecommendations(productId);
      }
    };

    // Option 1: Use requestIdleCallback (preferred - loads during browser idle time)
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        loadInBackground();
      }, { timeout: 2000 }); // Max 2s wait, then force load
    } else {
      // Option 2: Fallback for browsers without requestIdleCallback
      // Wait for page to be interactive, then load
      if (document.readyState === 'complete') {
        setTimeout(loadInBackground, 100);
      } else {
        window.addEventListener('load', () => {
          setTimeout(loadInBackground, 100);
        });
      }
    }

    // Option 3: Also observe for scroll (backup in case idle callback is delayed)
    // This ensures loading starts if user scrolls before idle callback fires
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);

        // Only load if not already loaded or loading
        if (!this.classList.contains('product-recommendations--loaded')) {
          this.loadRecommendations(productId);
        }
      },
      { rootMargin: "0px 0px 600px 0px" } // Start 600px before visible (generous margin)
    );

    this.observer.observe(this);
  }

  initializeImagePreload() {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.unobserve(this);
          const images = this.querySelectorAll('.swiper-slide img');
          [...images].forEach(img => this.preloadImage(img));
        },
        { rootMargin: "0px 0px 400px 0px" }
    );
    this.observer.observe(this);
  }

  preloadImage(img) {
    img.removeAttribute('loading');
  }

  loadRecommendations(productId) {
    // Prevent duplicate fetches
    if (this.loading || this.classList.contains('product-recommendations--loaded')) {
      return;
    }

    this.loading = true;

    if (typeof yotpoWidgetsContainer !== 'undefined' && yotpoWidgetsContainer.initWidgets) {
      fetch(
          `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`
      )
          .then((response) => response.text())
          .then((text) => {
            const html = document.createElement("div");
            html.innerHTML = text;
            const recommendations = html.querySelector("product-recommendations");

            if (recommendations?.innerHTML.trim().length) {
              // Remove skeleton and replace with actual content
              this.innerHTML = recommendations.innerHTML;
            } else {
              // No recommendations found, remove skeleton
              this.innerHTML = '';
            }

            this.classList.add("product-recommendations--loaded");
            this.loading = false;

            yotpoWidgetsContainer.initWidgets(false);

            this.initializeImagePreload();
            this.initSwiper();

            const swatchGroups = this.querySelectorAll(".o-swatches__swatch-options");
            swatchGroups.forEach((group) => {
              const buttons = group.querySelectorAll("[data-colour-swatch-button]");
              if (buttons.length > 0) {
                buttons[0].classList.add("active");
              }
            });

            if (
                !this.querySelector("slideshow-component") &&
                this.classList.contains("complementary-products")
            ) {
              this.remove();
            }
          })
          .catch((e) => {
            console.error('Error loading product recommendations:', e);
            // Remove skeleton on error
            this.innerHTML = '';
            this.loading = false;
          });
    } else {
      setTimeout(() => {
        this.loading = false;
        this.loadRecommendations(productId);
      }, 200);
    }
  }

  initSwiper() {
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
    }

    if (window.innerWidth < 1024) {
      this.swiperInstance = new Swiper(".swiper-related", {
        slidesPerView: 1.65,
        spaceBetween: 12,
        centeredSlides: false,
        navigation: {
          nextEl: ".swiper-related .swiper-button-next",
          prevEl: ".swiper-related .swiper-button-prev",
        },
        pagination: {
          el: ".swiper-pagination",
          clickable: true,
        },
        scrollbar: {
          el: ".swiper-scrollbar",
          draggable: true,
        },
      });
    } else {
      this.swiperInstance = new Swiper(".swiper-related", {
        slidesPerView: 4.2,
        spaceBetween: 24,
        centeredSlides: false,
        navigation: {
          nextEl: ".swiper-related .swiper-button-next",
          prevEl: ".swiper-related .swiper-button-prev",
        },
        pagination: {
          el: ".swiper-pagination",
          clickable: true,
        },
        scrollbar: {
          el: ".swiper-scrollbar",
          draggable: true,
        },
      });
    }
  }

  onResize() {
    this.initSwiper();
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener(
      "storefront:signincompleted",
      this.handleStorefrontSignInCompleted.bind(this)
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter(
      (queueElement) => !queue.includes(queueElement)
    );
    const quickBulkElement =
      this.closest("quick-order-list") || this.closest("quick-add-bulk");
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.min_error.replace(
          "[min]",
          event.target.dataset.min
        )
      );
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.max_error.replace(
          "[max]",
          event.target.max
        )
      );
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.step_error.replace(
          "[step]",
          event.target.step
        )
      );
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}

/****************For mega menu**********************/
let items = document
  .querySelector(".list-menu--inline")
  .querySelectorAll("details");

items.forEach((item) => {
  let submenu = item.querySelector("ul");

  item.addEventListener("mouseover", () => {
    item.setAttribute("open", true);
    clearTimeout(item.leaveTimeout); // Clear any existing timeout
  });

  item.addEventListener("mouseleave", (event) => {
    item.leaveTimeout = setTimeout(() => {
      // Check if the mouse is not within the bounds of the item or submenu
      if (
        !isMouseWithinBounds(item, event.relatedTarget) &&
        (!submenu || !isMouseWithinBounds(submenu, event.relatedTarget))
      ) {
        item.removeAttribute("open");
      }
    }, 1000); // Adjust the delay as needed
  });

  if (submenu) {
    submenu.addEventListener("mouseenter", () => {
      clearTimeout(item.leaveTimeout); // Keep the submenu open when mouse enters
    });

    submenu.addEventListener("mouseleave", (event) => {
      item.leaveTimeout = setTimeout(() => {
        if (
          !isMouseWithinBounds(item, event.relatedTarget) &&
          !isMouseWithinBounds(submenu, event.relatedTarget)
        ) {
          item.removeAttribute("open");
        }
      }, 1000); // Match the delay with the parent item
    });
  }
});

// Function to check if the mouse is within the bounds of an element
function isMouseWithinBounds(element, target) {
  if (!element || !target) return false;
  return element.contains(target);
}

// Global click event listener to close submenu when clicking outside
document.body.addEventListener("click", (event) => {
  const isClickInsideMenu = event.target.closest(".list-menu--inline") !== null;

  if (!isClickInsideMenu) {
    items.forEach((item) => {
      if (!item.classList.contains("mobile-facets__disclosure") &&
        !item.classList.contains("mobile-facets__details")) {
        item.removeAttribute("open");
      }
    });
  }
});

/****************For about menu**********************/

document.addEventListener("DOMContentLoaded", function () {
  let items = document.querySelectorAll(".header__inline-menu details");

  items.forEach((item) => {
    // Function to open the details element
    function openDetails() {
      item.setAttribute("open", true);
    }

    // Function to close the details element
    function closeDetails() {
      item.removeAttribute("open");
    }

    // Open the details on mouseover
    item.addEventListener("mouseover", openDetails);

    // Close the details on mouseleave from the details element
    item.addEventListener("mouseleave", closeDetails);

    // Also need to handle mouseleave on the <ul> inside the details
    const list = item.querySelector("ul");
    if (list) {
      list.addEventListener("mouseleave", closeDetails);
    }
  });
});

/****************product-accordion**********************/
// document.addEventListener("DOMContentLoaded", function () {
//   // (Optional) Activate an item if it has the class "is-active"
//   var activeItems = document.querySelectorAll(
//     ".accordion > .accordion-item.is-active"
//   );
//   activeItems.forEach(function (item) {
//     var panel = item.querySelector(".accordion-panel");
//     if (panel) {
//       panel.style.display = "block"; // Show panel
//     }
//   });

//   // Add click event listener to each accordion item
//   var accordionItems = document.querySelectorAll(
//     ".accordion > .accordion-item"
//   );
//   accordionItems.forEach(function (item) {
//     item.addEventListener("click", function () {
//       var isActive = item.classList.contains("is-active");

//       // Hide all panels and remove 'is-active' class
//       accordionItems.forEach(function (otherItem) {
//         if (otherItem !== item) {
//           otherItem.classList.remove("is-active");
//           var panel = otherItem.querySelector(".accordion-panel");
//           if (panel) {
//             panel.style.display = "none"; // Hide panel
//           }
//         }
//       });

//       // Toggle the clicked item
//       if (isActive) {
//         item.classList.remove("is-active");
//         var panel = item.querySelector(".accordion-panel");
//         if (panel) {
//           panel.style.display = "none"; // Hide panel
//         }
//       } else {
//         item.classList.add("is-active");
//         var panel = item.querySelector(".accordion-panel");
//         if (panel) {
//           panel.style.display = "block"; // Show panel
//         }
//       }
//     });
//   });
// });

// Accordion functionality - works with both initial load and AJAX content
function initializeAccordions(container) {
  const context = container || document;
  const items = context.querySelectorAll(".accordion-item");

  const expand = (item) => {
    const panel = item.querySelector(".accordion-panel");
    item.classList.add("is-active");
    item.setAttribute("aria-expanded", "true");
    panel.style.padding = "1rem 0";

    requestAnimationFrame(() => {
      panel.style.maxHeight = panel.scrollHeight + "px";

      const imgs = panel.querySelectorAll("img");
      let loaded = 0;

      if (imgs.length) {
        imgs.forEach((img) =>
            img.addEventListener("load", () => {
              if (++loaded === imgs.length) {
                panel.style.maxHeight = panel.scrollHeight + 20 + "px";
              }
            }),
        );

        setTimeout(() => {
          panel.style.maxHeight = panel.scrollHeight + 20 + "px";
        }, 100);
      }
    });
  };

  const collapse = (item) => {
    const panel = item.querySelector(".accordion-panel");
    item.classList.remove("is-active");
    item.setAttribute("aria-expanded", "false");
    panel.style.maxHeight = "0px";
    panel.style.padding = "0";
  };

  items.forEach((item) => {
    const thumb = item.querySelector(".accordion-thumb");
    const panel = item.querySelector(".accordion-panel");

    // Skip if already initialized
    if (thumb.dataset.accordionInitialized) return;
    thumb.dataset.accordionInitialized = "true";

    // Check if this accordion should be open by default (has is-active class)
    const isDefaultOpen = item.classList.contains("is-active");

    if (!isDefaultOpen) {
      // Only set closed state if it's not supposed to be open
      panel.style.maxHeight = "0px";
      panel.style.overflow = "hidden";
      panel.style.padding = "0";
    } else {
      // Set proper styles for open state
      panel.style.overflow = "hidden";
      panel.style.padding = "1rem 0";
      // Set maxHeight based on actual content
      requestAnimationFrame(() => {
        panel.style.maxHeight = panel.scrollHeight + 20 + "px";
      });
    }

    thumb.addEventListener("click", () => {
      if (item.classList.contains("is-active")) {
        collapse(item);
      } else {
        expand(item);
      }
    });
  });

  // Only expand first item if this is the initial page load (not quick-add) and no items are already active
  const hasActiveItem = Array.from(items).some(item => item.classList.contains("is-active"));
  if (items.length && !container && !hasActiveItem) expand(items[0]);
}

// Initialize on DOMContentLoaded for initial page load
document.addEventListener("DOMContentLoaded", () => {
  initializeAccordions();
});

// Initialize when product-info is loaded via AJAX (e.g., in quick-add modal)
document.addEventListener("product-info:loaded", (event) => {
  const productInfo = event.target;
  if (productInfo && productInfo.tagName === "PRODUCT-INFO") {
    initializeAccordions(productInfo);
  }
});

// ++++++ swatches button border radious for card +++++
document.addEventListener("DOMContentLoaded", () => {
  const observer = new MutationObserver(() => {
    const buttons = document.querySelectorAll("[data-colour-swatch-button]");
    buttons.forEach((button) => {
      if (!button.dataset.listenerAdded) {
        button.addEventListener("click", handleButtonClick);
        button.addEventListener("mouseenter", handleButtonClick);
        button.dataset.listenerAdded = "true";
      }
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
});

document.addEventListener("DOMContentLoaded", () => {
  const swatchGroups = document.querySelectorAll(".card__swatches");

  swatchGroups.forEach(group => {
    const buttons = group.querySelectorAll("[data-colour-swatch-button]");
    if (buttons.length > 0) {
      buttons[0].classList.add("active");
    }
  });
});


const productRecommendations = document.querySelector("product-recommendation");

if (productRecommendations) {
  observer.observe(productRecommendations, {
    attributes: true,
    attributeFilter: ["class"],
  });
}



function handleButtonClick(event) {

  const button = event.currentTarget;

  const group = button.closest(".card__swatches, .o-swatches__swatch-options");
  if (!group) return;

  group.querySelectorAll("[data-colour-swatch-button]")
      .forEach(b => b.classList.remove("active"));
  button.classList.add("active");

  const card  = button.closest(".product-card-wrapper, .card, .product-card");
  if (!card) return;

  const img   = card.querySelector("img");
  if (!img) return;

  if (button.dataset.varsrcset) {
    img.srcset      = button.dataset.varsrcset;
    img.dataset.srcset = button.dataset.varsrcset;
  }
  if (button.dataset.varimage) {
    img.src         = button.dataset.varimage;
    img.dataset.src = button.dataset.varimage;
  }

  // Update quick-add button URL with selected variant
  const quickAddButton = card.querySelector('[data-product-url]');
  const variantUrl = button.dataset.variantUrl;
  if (quickAddButton && variantUrl) {
    quickAddButton.setAttribute('data-product-url', variantUrl);
  }
}



function handleScroll() {
  var stickies = document.querySelectorAll(".template-product");
  var scroll = window.scrollY || document.documentElement.scrollTop;

  const mainButton = document.querySelector(".product-form__submit");
  const scrollButton = document.querySelector(".product-form__submit.scroll-button");

  stickies.forEach(function (sticky) {
    if (scroll >= 1000) {
      sticky.classList.add("fixed-button");

      if (mainButton && scrollButton) {
        scrollButton.innerHTML = mainButton.innerHTML;
      }
    } else {
      sticky.classList.remove("fixed-button");
    }
  });
}

// Attach the scroll event listener
window.addEventListener("scroll", handleScroll);

document.addEventListener("DOMContentLoaded", (event) => {
  const elements = document.querySelectorAll("a.openPopup");
  elements.forEach((element) => {
    element.setAttribute("onclick", "togglePopup()");
  });
});

function togglePopup() {
  var content = document.querySelector(".popup-outer");

  if (content.classList.contains("show")) {
    content.classList.remove("show");
    // Remove the show class to hide the popup
  } else {
    content.classList.add("show");
    // Add the show class to display the popup
  }
}
document.addEventListener("DOMContentLoaded", function () {
  var content = document.querySelector(".popup-outer");
  if (content) {
    content.classList.remove("show");
  }
});

function updateHtmlClass() {
  const body = document.body;
  const html = document.documentElement;

  if (body.classList.contains("overflow-hidden-tablet")) {
    html.classList.add("overflow-hidden");
  } else {
    html.classList.remove("overflow-hidden");
  }
}

const observer = new MutationObserver((mutationsList) => {
  for (const mutation of mutationsList) {
    if (mutation.type === "attributes" && mutation.attributeName === "class") {
      updateHtmlClass();
    }
  }
});

observer.observe(document.body, { attributes: true });
updateHtmlClass();


// document.addEventListener("DOMContentLoaded", function() {

//   var elements = document.querySelectorAll(".stacked-mobile_grid ");
//   elements.forEach(function(element) {
//     let parent = element.parentNode;
//     while (parent && !parent.classList.contains("three-grid_stacked")) {
//       parent.style.height = "auto";
//       parent = parent.parentNode;
//     }
//   });
// });


// document.addEventListener("DOMContentLoaded", (event) => {
//   const searchDetails = document.getElementById("search-details");
//   const searchDiv = document.getElementById("search-div");
//   const checkScroll = () => {
//     if (window.scrollY > 100) {
//       searchDetails.open = true;
//       searchDiv.style.display = "block";
//     }
//   };
//   checkScroll();
//   window.addEventListener("scroll", () => {
//     setTimeout(checkScroll, 0);
//   });
// });


const button = document.querySelector(".product-form__submit.scroll-button");

if (button) {
    button.addEventListener("click", event => {
      event.preventDefault();
        const targetElement = document.querySelector(".product__info-container.product__column-sticky");
        if (targetElement) {
            const targetOffset = targetElement.offsetTop;
            window.scrollTo({
              top: targetOffset - 200,
              behavior: 'smooth'
            });
        }
    });
}

window.addEventListener("popstate", (event) => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
});

// window.addEventListener("pageshow", function (event) {
//   var historyTraversal = event.persisted,
//     perf = window.performance,
//     perfEntries =
//       perf && perf.getEntriesByType && perf.getEntriesByType("navigation"),
//     perfEntryType = perfEntries && perfEntries[0] && perfEntries[0].type;
//     //navigationType = perf && perf.navigation && perf.navigation.type;
//     console.log(event);
//     console.log(perf.getEntriesByType("navigation"));
//   if (
//     (historyTraversal ||
//     perfEntryType === "back_forward" ) && document.querySelector('.pagination-component')
//   ) {
//     setTimeout(() => {
//       document.querySelector('.pagination-component').scrollIntoView({ behavior: "smooth", block: "center" });
//     }, 500);
//   }
// });

const goToProduct = (url, page) => {
  if(typeof url === 'undefined') return;
  if(typeof page !== 'undefined' && window.location.href.indexOf('/collections/') > -1){
    const pageUrl = new URL(window.location.href);
    pageUrl.searchParams.set('page', parseInt(page));
    window.history.pushState({path:pageUrl.href}, '', pageUrl.href);
  }
  window.location.href = url;
}

const shadowHost = document.querySelector('shop-login-button');

if (shadowHost) {
  const shadowRoot = shadowHost.shadowRoot;

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        const button = shadowRoot.querySelector('button');
        if (button) {
          button.style.maxWidth = '100%';
          button.style.width = '100%';
          button.style.justifyContent = 'center';
          button.querySelector('span').style.fontSize = '14px';
          button.querySelector('span').style.lineHeight = '15.32px';
          button.querySelector('span').style.fontWeight = '400';
          button.querySelector('span').style.fontFamily = 'Haffer';
          button.querySelector('span').style.letterSpacing = 'initial';
          button.querySelector('span').innerHTML = 'Sign in with SHOP';
          observer.disconnect();
        }
      }
    });
  });

  observer.observe(shadowRoot, { childList: true, subtree: true });
}